import { type ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";

interface MotionInViewProps {
  children: ReactNode;
  className?: string;
  /** Stagger delay in seconds (e.g. index * 0.1 for a cascade). */
  delay?: number;
  /** Distance (px) the element rises from. */
  y?: number;
}

/**
 * Reveals a single element as it scrolls into view: rise + fade + a subtle
 * scale-up. Each instance observes itself, so items in a grid/list appear one
 * after another as you scroll to them. Renders statically for reduced motion.
 */
export function MotionInView({ children, className, delay = 0, y = 32 }: MotionInViewProps) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y, scale: 0.96 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.55, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}
